import { createContext, useCallback, useContext } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import { FormControl, FormHelperText, Stack } from '@mui/material';
import { type Control, Controller } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import type {
  MultiPhoneNumbersFieldFragment,
  Phone_Numbers_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { GET_PHONE_NUMBERS_TYPES } from '@modules/_shared/common/queries';
import {
  type RaSelectOption,
  SelectField,
} from '@modules/_shared/components/form/RaSelect';
import { MultiContactInfoField } from '@modules/_shared/components/MultiContactInfoField';
import { PhoneInput } from '@modules/_shared/components/PhoneInput';
import { formatPhone } from '@modules/_shared/utils/formatting';
import { formatValidPhoneNumberE164 } from '@modules/_shared/utils/phone';
import { useLocale } from '@shared/hooks/locale';

export const MULTI_PHONE_NUMBERS_FIELD_FRAGMENT = gql(/* GraphQL */ `
  fragment MultiPhoneNumbersField on phone_numbers {
    id
    number
    verified
    primary
    type
  }
`);

const CHECK_PHONE_NUMBER_EXISTS = gql(/* GraphQL */ `
  query CheckPhoneNumberExists($checks: phone_numbers_bool_exp!) {
    phone_numbers(where: $checks, limit: 1) {
      id
    }
  }
`);

type PhoneNumberItem = Omit<MultiPhoneNumbersFieldFragment, 'number'> & {
  value: string;
  phone_number_id?: string;
};

export type MultiPhoneNumbersFieldFormData = {
  phone_numbers: Array<Omit<PhoneNumberItem, 'id'>>;
};

const PhoneNumberEditForm: React.FC<{
  control: Control<MultiPhoneNumbersFieldFormData>;
  name: `phone_numbers.${number}`;
}> = ({ control, name }) => {
  const { t } = useLocale();
  const { phoneNumberTypes } = useContext(PhoneNumberTypesContext);

  return (
    <Stack spacing={1} sx={{ flexGrow: 1 }}>
      <Controller
        name={`${name}.value`}
        control={control}
        rules={{
          required: t('This field is required'),
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth error={!!error}>
            <PhoneInput
              {...field}
              size="small"
              variant="outlined"
              slotProps={{
                input: {
                  sx: { background: 'var(--mui-palette-background-paper)' },
                },
              }}
              fullWidth
            />
            <FormHelperText>{error?.message}</FormHelperText>
          </FormControl>
        )}
      />
      <Controller
        name={`${name}.type`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth error={!!error}>
            <SelectField field={field as any} options={phoneNumberTypes} />
            <FormHelperText>{error?.message}</FormHelperText>
          </FormControl>
        )}
      />
    </Stack>
  );
};

const PhoneNumberTypesContext = createContext<{
  phoneNumberTypes: RaSelectOption[];
}>({
  phoneNumberTypes: [],
});

type MultiPhoneNumbersFieldProps = {
  label?: string;
  required?: boolean;
  parentUnicityCheck?: { organisation_id: string } | { user_id: string };
};

export const MultiPhoneNumbersField: React.FC<MultiPhoneNumbersFieldProps> = ({
  label,
  required,
  parentUnicityCheck,
}) => {
  const { t } = useLocale();
  const [checkPhoneNumberExists] = useLazyQuery(CHECK_PHONE_NUMBER_EXISTS);
  const { data } = useQuery(GET_PHONE_NUMBERS_TYPES);
  const phoneNumberTypes =
    data?.phone_number_type_enum.map(type => ({
      value: type.value,
      label: type.value,
    })) ?? [];
  const formatPhoneNumber = useCallback((phone: string) => {
    return formatPhone(phone);
  }, []);

  const getSecondaryText = useCallback(
    (phone: PhoneNumberItem) => phone.type ?? t('Not set'),
    [t],
  );

  const validateValue = useCallback(
    async (item: PhoneNumberItem) => {
      const formattedPhoneNumber = formatValidPhoneNumberE164(item.value);
      if (formattedPhoneNumber == null) {
        return {
          message: t('Invalid phone number'),
          type: 'validate',
        };
      }

      if (parentUnicityCheck == null) {
        return null;
      }

      const { data: checkPhoneNumberExistsData } = await checkPhoneNumberExists(
        {
          variables: {
            checks: {
              ...Object.entries(parentUnicityCheck).reduce<
                Pick<Phone_Numbers_Bool_Exp, 'organisation_id' | 'user_id'>
              >((acc, [key, value]) => {
                acc[key as 'organisation_id' | 'user_id'] = { _eq: value };
                return acc;
              }, {}),
              number: { _eq: formattedPhoneNumber },
              id: {
                _nin:
                  item.phone_number_id != null ? [item.phone_number_id] : [],
              },
            },
          },
        },
      );

      if ((checkPhoneNumberExistsData?.phone_numbers.length ?? 0) > 0) {
        return {
          message: t('Phone number already exists'),
          type: 'server',
        };
      }

      return null;
    },
    [checkPhoneNumberExists, parentUnicityCheck, t],
  );

  return (
    <PhoneNumberTypesContext.Provider value={{ phoneNumberTypes }}>
      <MultiContactInfoField
        label={label ?? t('Phone numbers')}
        requiredMessage={
          required ? t('At least one phone number is required') : undefined
        }
        fieldName="phone_numbers"
        UpdateForm={PhoneNumberEditForm as any}
        addLabel={t('Add phone number')}
        formatItemValue={formatPhoneNumber}
        getSecondaryText={getSecondaryText}
        validateValue={validateValue}
      />
    </PhoneNumberTypesContext.Provider>
  );
};
